import { useState } from 'react';
import { useAccess } from '../contexts/AccessContext';
import { toast } from 'sonner';

export function InviteCodeGate() {
  const { validateCode, isLoading, error } = useAccess();
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    const trimmedCode = code.trim().toUpperCase();

    if (!trimmedCode) {
      setLocalError('Please enter an invite code');
      return;
    }

    if (trimmedCode.length < 6) {
      setLocalError('Invite code is too short');
      return;
    }

    const success = await validateCode(trimmedCode);

    if (success) {
      toast.success('Access granted! Welcome to Atlas.');
    }
  };

  const displayError = localError || error;

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4">
      {/* Logo and Branding */}
      <div className="mb-8 text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <svg
            className="w-12 h-12"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Shield shape */}
            <path
              d="M4 6C4 4.89543 4.89543 4 6 4H26C27.1046 4 28 4.89543 28 6V18C28 22.4183 24.4183 28 16 28C7.58172 28 4 22.4183 4 18V6Z"
              fill="#0e0f10"
              stroke="#3fb68b"
              strokeWidth="1.5"
            />
            {/* Inner chevron / A shape */}
            <path
              d="M16 8L22 20H18.5L16 15.5L13.5 20H10L16 8Z"
              fill="#3fb68b"
            />
            {/* Bottom accent bar */}
            <path
              d="M12 22H20"
              stroke="#3fb68b"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <span className="text-3xl font-bold text-white tracking-tight">Atlas</span>
        </div>
        <p className="text-[#6c7884] text-sm">
          HIP-3 Market Aggregator for Hyperliquid
        </p>
      </div>

      {/* Invite Code Card */}
      <div className="w-full max-w-md bg-[#0e0f10] border border-[#2d3748] rounded-lg p-6">
        <h2 className="text-white text-lg font-semibold mb-2 text-center">
          Enter Invite Code
        </h2>
        <p className="text-[#6c7884] text-sm text-center mb-6">
          Atlas is currently in private alpha. Enter your invite code to access the platform.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="invite-code"
              className="block text-[11px] font-semibold text-[#6c7884] uppercase tracking-wider mb-2"
            >
              Invite Code
            </label>
            <input
              id="invite-code"
              type="text"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                setLocalError(null);
              }}
              placeholder="ATLASXXX"
              className="w-full bg-[#0b0c0d] border border-[#2d3748] rounded-md px-4 py-3 text-[13px] text-white placeholder-[#4a5568] focus:outline-none focus:ring-2 focus:ring-[#2962ff]/30 focus:border-[#2962ff] transition-all"
              disabled={isLoading}
              autoFocus
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          {displayError && (
            <p className="text-[#f6465d] text-[12px] text-center">
              {displayError}
            </p>
          )}

          <button
            type="submit"
            disabled={isLoading || !code.trim()}
            className="w-full bg-[#2962ff] hover:bg-[#1e4fd8] disabled:bg-[#2962ff]/50 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-md transition-all text-[13px] flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Validating...
              </>
            ) : (
              'Enter Atlas'
            )}
          </button>
        </form>
      </div>

      {/* Features Preview */}
      <div className="mt-8 grid grid-cols-3 gap-6 max-w-lg">
        <div className="text-center">
          <div className="w-10 h-10 mx-auto mb-2 rounded-lg bg-[#0e0f10] border border-[#2d3748] flex items-center justify-center">
            <svg className="w-5 h-5 text-[#2962ff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <p className="text-[11px] text-[#6c7884]">Smart Routing</p>
        </div>
        <div className="text-center">
          <div className="w-10 h-10 mx-auto mb-2 rounded-lg bg-[#0e0f10] border border-[#2d3748] flex items-center justify-center">
            <svg className="w-5 h-5 text-[#2962ff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-[11px] text-[#6c7884]">Aggregated Data</p>
        </div>
        <div className="text-center">
          <div className="w-10 h-10 mx-auto mb-2 rounded-lg bg-[#0e0f10] border border-[#2d3748] flex items-center justify-center">
            <svg className="w-5 h-5 text-[#2962ff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <p className="text-[11px] text-[#6c7884]">Best Execution</p>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-12 text-center">
        <p className="text-[11px] text-[#4a5568]">
          Need an invite code?{' '}
          <a
            href="https://twitter.com/atlas_xyz"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#2962ff] hover:underline"
          >
            Follow us on X
          </a>
        </p>
      </div>
    </div>
  );
}
